import type { LogAlertsTransport } from '@speles7172/log-console';
import type {
  LogAlertAssistRequest,
  LogAlertAssistResult,
  LogAlertFiring,
  LogAlertRule,
  LogAlertRuleInput,
  LogAlertRulePatch,
  LogAlertTestResult,
  LogAlertsInfo,
  LogDailySummary,
} from '@speles7172/log-client/core';
import { extractApiErrorMessage } from '../utils/apiError';
import { apiClient } from './client';

/**
 * System → Alerts' transport (`LogAlertsTransport`), over `/admin/log-alerts`
 * — log-client's alert engine on the server (`services/log-alerts.service.ts`),
 * which decides who may call it (administrators), validates every rule and
 * audits every change.
 *
 * A module-level object, so its identity never changes: the console keys its
 * loads on the transport, and a new object per render would reload forever.
 */
const BASE = '/admin/log-alerts';
const rulePath = (id: string) => `${BASE}/rules/${encodeURIComponent(id)}`;

/** The console shows a rejection's message, so it should be the server's, not Axios's. */
async function said<T>(work: Promise<{ data: T }>): Promise<T> {
  try {
    return (await work).data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'The request failed. Try again.'));
  }
}

export const getLogAlertsInfo = () => said(apiClient.get<LogAlertsInfo>(`${BASE}/info`));

export const listLogAlertRules = () => said(apiClient.get<LogAlertRule[]>(`${BASE}/rules`));

export const createLogAlertRule = (input: LogAlertRuleInput) =>
  said(apiClient.post<LogAlertRule>(`${BASE}/rules`, input));

/** A partial edit, carrying the rule's `version`: an edit made on a stale copy is refused (409). */
export const updateLogAlertRule = (id: string, patch: LogAlertRulePatch) =>
  said(apiClient.patch<LogAlertRule>(rulePath(id), patch));

export async function deleteLogAlertRule(id: string): Promise<void> {
  await said(apiClient.delete(rulePath(id)));
}

export const listLogAlertFirings = () =>
  said(apiClient.get<LogAlertFiring[]>(`${BASE}/firings`, { params: { limit: 50 } }));

export const testLogAlertRule = (input: LogAlertRuleInput) =>
  said(apiClient.post<LogAlertTestResult>(`${BASE}/test`, input));

export const assistLogAlertRule = (request: LogAlertAssistRequest) =>
  said(apiClient.post<LogAlertAssistResult>(`${BASE}/assist`, request));

export const previewLogSummary = () => said(apiClient.get<LogDailySummary>(`${BASE}/summary`));

/** Resolves once the server has taken it (202); the summary goes out after the request. */
export async function sendLogSummary(): Promise<void> {
  await said(apiClient.post(`${BASE}/summary/send`, {}));
}

export const logAlertsTransport: LogAlertsTransport = {
  info: getLogAlertsInfo,
  listRules: listLogAlertRules,
  createRule: createLogAlertRule,
  updateRule: updateLogAlertRule,
  deleteRule: deleteLogAlertRule,
  listFirings: listLogAlertFirings,
  testRule: testLogAlertRule,
  assist: assistLogAlertRule,
  previewSummary: previewLogSummary,
  sendSummary: sendLogSummary,
};
